import { useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@/routes/hooks';
import { useAuthContext } from '@/auth/hooks';
import { paths } from '@/routes/paths';
import { ContactStatus, IContactTableFilters } from '@/types/entities/contacts';
import { OrionModule, OrionAccess, OrionSubmodule } from '@/types';
import { debounce } from 'lodash';
import {
  deleteContact,
  segmentsApiService,
  countriesApiService,
  getFilteredContacts,
  lifeCyclesApiService,
} from '@/services';
import { useListLayout } from '@/hooks/lists/use-list-layout';

interface UseOrganizationContactListProps {
  organizationId?: string;
}

const fetchCountriesData = async () => countriesApiService.getAll();

const fetchLifeCycles = async () => lifeCyclesApiService.getAll();

const fetchSegments = async () => segmentsApiService.getAll();

const defaultFilters: IContactTableFilters = {
  search: '',
  page: 0,
  pageSize: 100,
  countries: [],
  lifeCycles: [],
  status: [{ id: ContactStatus.active, label: 'Active' }],
  segments: [],
};

export function useOrganizationContactList({
  organizationId,
}: UseOrganizationContactListProps = {}) {
  const router = useRouter();
  const { user } = useAuthContext();

  const listLayout = useListLayout({
    entityName: 'organizationContact',
    defaultFilters,
  });

  const { filters, page, rowsPerPage } = listLayout;

  const searchTextRef = useRef<HTMLInputElement>(null);

  // User permissions - memoized using OrionModule, OrionSubmodule, and OrionAccess constants
  const organizationContactPermissions = useMemo(
    () => ({
      canCreate: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.CONTACT,
        OrionAccess.CREATE
      ),
      canUpdate: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.CONTACT,
        OrionAccess.UPDATE
      ),
      canDelete: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.CONTACT,
        OrionAccess.DELETE
      ),
      canView: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.CONTACT,
        OrionAccess.VIEW
      ),
      isAdmin: !!user?.isAdmin,
    }),
    [user]
  );

  // Get organization contact-specific permissions using OrionModule, OrionSubmodule, and OrionAccess constants
  const getOrganizationContactPermissions = useCallback(
    (contact: any) => ({
      canUpdate: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.CONTACT,
        OrionAccess.UPDATE
      ),
      canDelete: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.CONTACT,
        OrionAccess.DELETE
      ),
      canUpdateItem: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.CONTACT,
        OrionAccess.UPDATE,
        contact?.organization?.owner ?? ''
      ),
      canCreateUser: !contact.userExists && contact.status === 'active',
      isAdmin: !!user?.isAdmin,
    }),
    [user]
  );

  // Memoize canReset calculation
  const canReset = useMemo(
    () =>
      filters.countries.length > 0 ||
      filters.lifeCycles.length > 0 ||
      filters.status.length > 0 ||
      filters.segments.length > 0 ||
      (filters.search?.length ?? 0) > 0,
    [filters]
  );

  // Build search data
  const searchData = useMemo(() => {
    const data: any = {
      page: (page + 1).toString(),
      pageSize: rowsPerPage.toString(),
    };

    if (organizationId) {
      data.organization = organizationId;
    }

    if (filters.search.length) {
      data.search = filters.search;
    }

    if (filters.countries.length > 0) {
      data.countries = filters.countries.map((item: any) => item.id);
    }

    if (filters.lifeCycles.length > 0) {
      data.lifeCycles = filters.lifeCycles.map((item: any) => item.id);
    }

    if (filters.status.length > 0) {
      data.status = filters.status.map((item: any) => item.id);
    }

    if (filters.segments.length > 0) {
      data.segments = filters.segments; // segments is already an array of strings
    }

    return { data };
  }, [page, rowsPerPage, filters, organizationId]);

  const fetchData = useCallback(
    async (filterData: any) => getFilteredContacts(searchData.data),
    [searchData]
  );

  // Main data query
  const { isLoading, data, refetch } = useQuery(
    [
      `organizationContact${organizationId ? `-${organizationId}` : ''}List`,
      filters.search,
      page,
      rowsPerPage,
      filters.countries,
      filters.lifeCycles,
      filters.status,
      filters.segments,
    ],
    () => fetchData(filters),
    {
      keepPreviousData: true,
      staleTime: 5000,
      enabled: !!organizationId,
    }
  );

  // Supporting data queries
  const { data: countriesData } = useQuery(['countriesAll'], fetchCountriesData);
  const { data: lifeCyclesData } = useQuery(['lifeCyclesAll'], fetchLifeCycles);
  const { data: segmentsData } = useQuery(['segmentsAll'], fetchSegments);

  const handleEditRow = useCallback(
    (id: string) => {
      if (organizationId) {
        router.push(paths.management.accounts.organizations.tabEditContact(organizationId, id));
      }
    },
    [organizationId, router]
  );

  const handleDeleteRow = useCallback(
    async (id: string) => {
      await deleteContact(id);
      refetch();
    },
    [refetch]
  );

  // Navigation handlers
  const handleGoToDetail = useCallback(
    (contact: any) => {
      if (organizationId) {
        router.push(
          paths.management.accounts.organizations.tabEditContact(organizationId, contact._id)
        );
      }
    },
    [organizationId, router]
  );

  const handleCreateUser = useCallback(
    (contact: any) => {
      sessionStorage.setItem('contactData', JSON.stringify(contact));
      router.push(paths.setting.directory.usermkt.new);
    },
    [router]
  );

  // Handler to clear all filters
  const handleClearFilters = useCallback(() => {
    listLayout.handleFilters('countries', []);
    listLayout.handleFilters('lifeCycles', []);
    listLayout.handleFilters('status', []);
    listLayout.handleFilters('segments', []);
  }, [listLayout]);

  // Custom filter search with debounce
  const handleFilterSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      listLayout.handleFilters('search', event.target.value);
    },
    [listLayout]
  );

  const debouncedHandleFilterSearch = useMemo(
    () => debounce(handleFilterSearch, 200),
    [handleFilterSearch]
  );

  return {
    // List layout functionality
    ...listLayout,

    // Data
    data,
    countriesData: countriesData?.data || [],
    lifeCyclesData: lifeCyclesData?.data || [],
    segmentsData: segmentsData?.data || [],

    // User permissions
    organizationContactPermissions,
    getOrganizationContactPermissions,
    // Backward compatibility
    userCanCreate: organizationContactPermissions.canCreate,
    userCanUpdate: organizationContactPermissions.canUpdate,
    userCanDelete: organizationContactPermissions.canDelete,
    userCanView: organizationContactPermissions.canView,

    // Computed values
    canReset,

    // Handlers
    handleEditRow,
    handleDeleteRow,
    handleGoToDetail,
    handleCreateUser,
    handleClearFilters,
    handleFilterSearch: debouncedHandleFilterSearch,

    // Loading states
    isLoading,

    // Refs
    searchTextRef,

    // Utils
    refetch,
  };
}
